import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { hash } from 'spark-md5';

/**
 * Normalizes a string for comparison by:
 * - Converting to lowercase
 * - Removing special characters and spaces
 * - Keeping only alphanumeric characters
 */
const normalizeString = str => {
  if (!str || typeof str !== 'string') return '';
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Calculates Levenshtein distance between two strings
 * Returns a similarity score between 0 (completely different) and 1 (identical)
 */
const levenshteinSimilarity = (str1, str2) => {
  const s1 = normalizeString(str1);
  const s2 = normalizeString(str2);
  if (s1 === s2) return 1.0;
  if (s1.length === 0) return s2.length === 0 ? 1.0 : 0.0;
  if (s2.length === 0) return 0.0;
  const matrix = [];
  const len1 = s1.length;
  const len2 = s2.length;

  // Initialize matrix
  for (let i = 0; i <= len1; i += 1) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j += 1) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i += 1) {
    for (let j = 1; j <= len2; j += 1) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1,
      // deletion
      matrix[i][j - 1] + 1,
      // insertion
      matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  const distance = matrix[len1][len2];
  const maxLength = Math.max(len1, len2);
  return 1 - distance / maxLength;
};

/**
 * Calculates a combined similarity score using multiple strategies:
 * 1. Exact match (case-insensitive) - highest priority
 * 2. Normalized exact match (alphanumeric only) - high priority
 * 3. Levenshtein similarity - medium priority
 * @param {string} csvTitle - The CSV column title
 * @param {string} fieldTitle - The form field title
 * @param {number} minSimilarity - Minimum similarity threshold (default: 0.8)
 * @returns {Object} Object with score and method, or { score: 0, method: 'none' }
 */
const calculateSimilarity = function calculateSimilarity(csvTitle, fieldTitle) {
  let minSimilarity = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0.8;
  // Exact match (case-insensitive)
  if (csvTitle.toLowerCase() === fieldTitle.toLowerCase()) {
    return {
      score: 1.0,
      method: 'exact'
    };
  }

  // Normalized exact match
  const normalizedCsv = normalizeString(csvTitle);
  const normalizedField = normalizeString(fieldTitle);
  if (normalizedCsv === normalizedField && normalizedCsv.length > 0) {
    return {
      score: 0.95,
      method: 'normalized'
    };
  }

  // Levenshtein similarity
  const levenshteinScore = levenshteinSimilarity(csvTitle, fieldTitle);
  if (levenshteinScore >= minSimilarity) {
    return {
      score: levenshteinScore,
      method: 'levenshtein'
    };
  }
  return {
    score: 0,
    method: 'none'
  };
};

/**
 * Auto-matches CSV column headers to form fields based on similarity
 * @param {Object} headerObjects - Object mapping header keys to header objects
 * @param {Object} formContentPath - Object mapping field keys to field objects
 * @param {number} minSimilarity - Minimum similarity score (0-1). Default: 0.8
 * @returns {Object} Updated headerObjects with mappedContentPath added
 */
const autoMatchCSVColumns = function autoMatchCSVColumns(headerObjects, formContentPath) {
  let minSimilarity = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0.8;
  if (!formContentPath || typeof formContentPath !== 'object') {
    return headerObjects;
  }
  const formFields = Object.values(formContentPath).filter(field => field && field.title && field.contentPath);
  if (formFields.length === 0) {
    return headerObjects;
  }
  const updatedHeaders = _objectSpread({}, headerObjects);
  const usedFields = new Set();

  // First pass: exact matches (highest priority)
  Object.keys(updatedHeaders).forEach(headerKey => {
    const header = updatedHeaders[headerKey];
    const csvTitle = header.title;

    // Skip if already mapped
    if (header.mappedContentPath && Object.keys(header.mappedContentPath).length > 0) {
      return;
    }

    // Find exact match
    const exactMatch = formFields.find(field => !usedFields.has(field.contentPath) && field.title.toLowerCase() === csvTitle.toLowerCase());
    if (exactMatch) {
      const fieldHash = hash(exactMatch.title);
      updatedHeaders[headerKey].mappedContentPath = {
        [fieldHash]: _objectSpread(_objectSpread(_objectSpread({
          title: exactMatch.title,
          contentPath: exactMatch.contentPath,
          fieldType: exactMatch.fieldType || 'string',
          // eslint-disable-next-line no-underscore-dangle
          _created: exactMatch._created || 0
        }, exactMatch.breadcrumbs != null && {
          breadcrumbs: exactMatch.breadcrumbs
        }), exactMatch.formatOptions != null && {
          formatOptions: exactMatch.formatOptions
        }), exactMatch.conversionFunctions != null && {
          conversionFunctions: exactMatch.conversionFunctions
        })
      };
      usedFields.add(exactMatch.contentPath);
    }
  });

  // Second pass: similarity-based matches
  Object.keys(updatedHeaders).forEach(headerKey => {
    const header = updatedHeaders[headerKey];
    const csvTitle = header.title;

    // Skip if already mapped
    if (header.mappedContentPath && Object.keys(header.mappedContentPath).length > 0) {
      return;
    }

    // Calculate similarity for all unused fields
    const similarities = formFields.filter(field => !usedFields.has(field.contentPath)).map(field => ({
      field,
      similarity: calculateSimilarity(csvTitle, field.title, minSimilarity)
    })).filter(item => item.similarity.score >= minSimilarity).sort((a, b) => b.similarity.score - a.similarity.score);

    // Use the best match if available
    if (similarities.length > 0) {
      const bestMatch = similarities[0];
      const matchedField = bestMatch.field;
      const fieldHash = hash(matchedField.title);
      updatedHeaders[headerKey].mappedContentPath = {
        [fieldHash]: _objectSpread(_objectSpread(_objectSpread({
          title: matchedField.title,
          contentPath: matchedField.contentPath,
          fieldType: matchedField.fieldType || 'string',
          // eslint-disable-next-line no-underscore-dangle
          _created: matchedField._created || 0
        }, matchedField.breadcrumbs != null && {
          breadcrumbs: matchedField.breadcrumbs
        }), matchedField.formatOptions != null && {
          formatOptions: matchedField.formatOptions
        }), matchedField.conversionFunctions != null && {
          conversionFunctions: matchedField.conversionFunctions
        })
      };
      usedFields.add(matchedField.contentPath);
    }
  });
  return updatedHeaders;
};
export default autoMatchCSVColumns;